import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Report, ReportStatus } from "../../api/models/report.models";
import ContainerHeader from "../../components/containerHeader";
import { Company } from "../../api/models/companies.models";
import ReportsTable from "../../views/reportsTable";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";
import useAdminReportCommands from "../../hooks/useAdminReportCommands";
import useAllUsers from "../../hooks/useAllUsers";
import AdminCombinedReportView from "./adminCombinedReportView";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../../stores/ui-store";
import { AdminReportFilters, UIState } from "../../types/ui-types";
import AdminReportFiltersComponent from "../../components/adminReportFilters";
import useAdminAllReports from "../../hooks/useAdminAllReports";
import { getReportTotalWeight } from "../../utils/rowUtils";
import { useSearchParams } from "react-router";
import { useAdminDataContext } from "../../contexts/adminDataContext";

const AllReportsContainer = styled.div`
  position: relative;
`;

const PaddedContainer = styled.div`
  padding: 6rem 2rem;
  text-align: left;
`;

const AdminReportView: React.FC = () => {
  const { t } = useTranslation();
  const hasPreselectedCompanyRef = useRef(false);
  const [searchParams] = useSearchParams();

  // Get companyId from query string if present
  const companyIdFromQuery = searchParams.get("companyId");
  const organizationIdFromQuery = searchParams.get("organizationId");

  const [allData, setAllData] = useState<Report[]>([]);
  const [tableData, setTableData] = useState<
    (Report & {
      yearQuarterMonth?: string;
      companyName?: string;
      organizationName?: string;
      totalWeight?: number;
      internalMessagesCount?: number;
    })[]
  >([]);

  const [filters, setSelectedAdminFilters] = useUIStore(
    useShallow((state: UIState) => [
      state.selectedAdminFilters,
      state.setSelectedAdminFilters,
    ]),
  );

  const [combinedViewSelection, setCombinedViewSelection] = useState<Report[]>(
    [],
  );

  const { companies, isLoadingCompanies } = useAdminDataContext();

  const { users, usersIsLoading } = useAllUsers();

  const { reportsData, isLoadingReports } = useAdminAllReports({
    filters: filters,
    enabled: hasPreselectedCompanyRef?.current,
  });

  // Preselect company for filters if companyId is present in query params
  useEffect(() => {
    if (!hasPreselectedCompanyRef.current && companies && !isLoadingCompanies) {
      if (companyIdFromQuery) {
        const company = companies.find(
          (c) => c.id === Number(companyIdFromQuery),
        );

        const organizationIdNum = organizationIdFromQuery
          ? Number(organizationIdFromQuery)
          : null;

        if (company) {
          if (organizationIdNum) {
            const organization = company.organizations?.find(
              (org) => org.id === organizationIdNum,
            );
            setSelectedAdminFilters({
              ...filters,
              company: company,
              organization: organization,
              skip: filters.skip ?? 0,
              take: filters.take ?? 50,
            });
          } else {
            setSelectedAdminFilters({
              ...filters,
              company: company,
              skip: filters.skip ?? 0,
              take: filters.take ?? 50,
            });
          }
        } else {
          setSelectedAdminFilters({
            ...filters,
            skip: filters.skip ?? 0,
            take: filters.take ?? 50,
          });
        }
      } else {
        // No query params, just ensure filters have default values
        setSelectedAdminFilters({
          ...filters,
          skip: filters.skip ?? 0,
          take: filters.take ?? 50,
        });
      }

      hasPreselectedCompanyRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, isLoadingCompanies, companyIdFromQuery]);

  useEffect(() => {
    if (reportsData && companies) {
      const formattedData = reportsData.data.map((item) => {
        const company = companies?.find((c) => c.id === item.report.companyId);
        const companyOrgs = company?.organizations?.length
          ? company.organizations
          : item.report.companyId === filters.company?.id
            ? filters.company?.organizations
            : [];
        const organization = companyOrgs?.find(
          (org) => org.id === item.report.organizationId,
        );

        // Format yearQuarterMonth string
        let yearQuarterMonth = `${item.report.year}`;
        if (item.report.quarter) {
          yearQuarterMonth += `-Q${item.report.quarter}`;
        }
        if (item.report.month) {
          yearQuarterMonth += `-M${item.report.month}`;
        }

        return {
          ...item.report,
          companyName: company?.companyName || item.company.companyName,
          organizationName: organization?.organizationName,
          yearQuarterMonth,
          totalWeight: getReportTotalWeight(
            item.report.rows,
            true, // skip reusable rows for total weight calculation for admin view
            item.report.reportType,
          ),
          internalMessagesCount: item.internalMessagesCount,
        };
      });

      setTableData(formattedData);
      setAllData(formattedData);
    }
  }, [reportsData, companies, filters.company]);

  const { updateReportStatus } = useAdminReportCommands();

  const handleFilterChange = (filters: AdminReportFilters) => {
    setSelectedAdminFilters(filters);
  };

  const handleReturnToEdit = (reportId: number) => {
    updateReportStatus({ id: reportId, status: ReportStatus.Draft });
  };

  const handleSelectCombinedViewReport = (
    report: Report & { yearQuarterMonth?: string },
  ) => {
    // Find matching reports where organizationId is not null
    const matchingReports = tableData?.filter((r) => {
      return (
        r.yearQuarterMonth === report.yearQuarterMonth &&
        r.organizationId !== null
      );
    });

    setCombinedViewSelection(matchingReports || []);
  };

  const setPage = (newPage: number) => {
    setSelectedAdminFilters({
      ...filters,
      skip: (newPage - 1) * (filters.take || 50),
    });
  };

  const setRows = (newRows: number) => {
    setSelectedAdminFilters({
      ...filters,
      take: newRows,
      skip: 0,
    });
  };

  const companyOrganizations = useMemo(() => {
    const company = companies?.find((c) => c.id === filters.company?.id);
    // Fall back to organizations from the stored filter company object
    // in case the context cache doesn't have organizations populated yet
    const organizations = company?.organizations?.length
      ? company.organizations
      : filters.company?.organizations;
    return organizations?.sort((a, b) =>
      a.organizationName.localeCompare(b.organizationName),
    );
  }, [companies, filters.company?.id, filters.company?.organizations]);

  return (
    <AllReportsContainer>
      <ContainerHeader
        description={t("subheader-all-reports")}
        title={t("header-all-reports")}
      />
      {combinedViewSelection?.length === 0 ? (
        <PaddedContainer>
          <LoadingContainer
            status={
              isLoadingCompanies || usersIsLoading
                ? HttpRequestStatus.LOADING
                : HttpRequestStatus.SUCCESS
            }
          >
            <AdminReportFiltersComponent
              onFiltersChange={handleFilterChange}
              users={users}
              companies={companies}
              companyOrganizations={companyOrganizations}
            />

            <LoadingContainer
              status={
                isLoadingReports
                  ? HttpRequestStatus.LOADING
                  : HttpRequestStatus.SUCCESS
              }
            >
              <ReportsTable
                reports={tableData || []}
                adminMode={true}
                returnToEditReport={(id: number) => handleReturnToEdit(id)}
                handleSelectCombinedViewReport={handleSelectCombinedViewReport}
                groupRowsBy={filters.company ? "yearQuarterMonth" : ""}
                company={filters.company as Company}
                organizations={filters.company?.organizations || undefined}
                allReports={allData}
                showPagination={true}
                rows={filters.take}
                page={
                  Math.floor((filters.skip || 0) / (filters.take || 50)) + 1
                }
                setPage={setPage}
                setRows={setRows}
                totalReports={reportsData?._count}
                showOrganizationName
                showCompanyName
                showTotalWeight
                showReportType
              />
            </LoadingContainer>
          </LoadingContainer>
        </PaddedContainer>
      ) : (
        <AdminCombinedReportView
          company={filters.company as Company}
          hide={() => setCombinedViewSelection([])}
          year={combinedViewSelection[0].year}
          quarter={combinedViewSelection[0].quarter}
          month={combinedViewSelection[0].month}
          prevYearReportsData={allData?.filter((report) => {
            return (
              report.year === (combinedViewSelection[0].year as number) - 1 &&
              report.companyId === (filters.company as Company).id &&
              report.organizationId !== null
            );
          })}
          reportsData={combinedViewSelection}
        />
      )}
    </AllReportsContainer>
  );
};

export default AdminReportView;
